import { Alert, Box, Skeleton, Stack, Typography } from '@mui/material';
import { SnackbarKey, useSnackbar } from 'notistack';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import useWaiverToasts from 'modules/Onboarding/AccountingSetup/StatementUpload/WaiverToasts';
import StatementAccountCard from 'modules/common/components/VerifiedStatementUpload/components/StatementAccountCard';
import UndoWaiver from 'modules/Onboarding/AccountingSetup/StatementUpload/UndoWaiver';
import { StatementFile } from 'modules/common/components/VerifiedStatementUpload/components/StatementSlotList';
import { STATEMENT_DOCUMENT_TYPE } from 'modules/common/components/VerifiedStatementUpload/components/StatementUploadArea';
import StatementProcessingBanner from 'modules/Onboarding/AccountingSetup/StatementUpload/StatementProcessingBanner';
import useAwaitingStatements from 'modules/common/components/VerifiedStatementUpload/hooks/useAwaitingStatements';
import VerifiedStatementUploadModal from 'modules/common/components/VerifiedStatementUpload/VerifiedStatementUploadModal';
import { filesUploadedElsewhere } from 'modules/common/components/VerifiedStatementUpload/utils/filesUploadedElsewhere';
import { formatMonthRange } from 'modules/common/components/VerifiedStatementUpload/utils/formatMonthList';
import {
    accountKey,
    groupHeldPeriods,
    statementAccountLabel,
} from 'modules/common/components/VerifiedStatementUpload/utils/periods';
import { Document } from 'modules/common/interfaces/documents';
import useStatementStepWrites from 'modules/Onboarding/AccountingSetup/StatementUpload/hooks/useStatementStepWrites';
import { useDocumentFiltered } from 'modules/common/queries/documents';
import { StatementPeriodAssignment } from 'services/bookkeeping/mutations/useReassignStatementPeriods';
import useStatementCoverage, {
    AccountStatementCoverage,
    COVERAGE_UNAVAILABLE_MESSAGE,
    isCoverageUsable,
    StatementPeriod,
} from 'services/bookkeeping/queries/useStatementCoverage';

/**
 * Long enough to read two sentences and decide to take the waiver back.
 *
 * The provider's 3s default is tuned for toasts with nothing to act on. An Undo that expires
 * before the member has finished reading the message offering it is the same as no Undo at all.
 *
 * Longer than reading time alone would need, because this is an actionable control inside a
 * `role="alert"` region that hides itself — WCAG 2.2.1. A member still hearing the announcement,
 * or reaching for the button slowly, should not lose the only surfaced way to reverse the waiver.
 * Not `persist`: the fallback is real — the card's menu untick does the same thing — so the toast
 * should still get out of the way on its own.
 */
export const WAIVER_SNACKBAR_DURATION_MS = 20000;

/**
 * The design frame's width, which is what makes this message wrap to two lines.
 *
 * notistack only caps its container at `calc(100% - 40px)`, so left alone a message this long
 * renders as one line the width of the viewport. A max rather than a fixed width: it matches the
 * design wherever there is room and still shrinks inside notistack's own cap on a narrow screen.
 */
export const WAIVER_SNACKBAR_MAX_WIDTH = 600;

/** Exported so the test asserts the message the member reads, not a second copy of it. */
export const waiverToastMessage = (accountLabel: string) =>
    `${accountLabel} marked as unavailable. We'll follow up to request these statements later.`;

type StepUpload = ReturnType<typeof useStatementStepWrites>['uploadedStatements'][number];

/** A step upload as the modal and the processing banner list it. */
function toStatementFile(entry: StepUpload): StatementFile {
    return { documentUuid: entry.member_document_id, fileName: entry.file_name, uploadedAt: entry.uploaded_at };
}

/**
 * The bank-statement half of the Accounting Setup document step, coverage-driven.
 *
 * Owns which uploads belong to this step. That list lives in the `statement_upload` step's
 * existing `uploaded_statements` slot rather than anywhere new — it is what lets a
 * returning member see "this file was rejected" without any verdict being stored: a
 * document listed here but missing from the coverage endpoint's held periods failed, and
 * why is recomputed from its automation job.
 *
 * Does NOT report Continue-readiness upward — the step derives that itself from the same
 * queries via `useStatementReadiness`.
 */
export default function VerifiedStatementUploadSection() {
    const { enqueueSnackbar } = useSnackbar();
    const { data: coverage, isPending: isCoveragePending } = useStatementCoverage();
    const { data: documents = [] } = useDocumentFiltered({ document_type: STATEMENT_DOCUMENT_TYPE }, {});
    const {
        uploadedStatements,
        acknowledgedAccounts,
        saveUploads,
        removeUpload,
        savePeriods,
        saveAcknowledged,
        refreshCoverageIfUploaded,
    } = useStatementStepWrites();

    const [activeAccountKey, setActiveAccountKey] = useState<string | null>(null);

    /**
     * Waiver toasts still on screen. Owned by the step, not by this section.
     *
     * Keyed by account rather than held as a flat set, because a toast is stale the moment the
     * member acts on that same account again. It lives a level up so the step can also retire
     * them when completion *starts* — see `WaiverToasts`, which carries the rest of the reasoning.
     */
    const waiverToasts = useWaiverToasts();

    /**
     * Whether this section is still on screen, read after every await that ends in a toast.
     *
     * The cleanup below can only close toasts that already exist. A write still in flight when the
     * section unmounts resolves afterwards, and the toast it would then raise is one the cleanup
     * has already run past — root-level, with a live Undo, and nothing left to take it down.
     */
    const isMounted = useRef(true);

    useEffect(() => {
        isMounted.current = true;
        return () => {
            isMounted.current = false;
            // Still here as well as at completion: navigating away by any other route — back, the
            // browser, a step the member abandons — leaves toasts the step never got to retire.
            waiverToasts.dismissAll();
        };
    }, [waiverToasts]);

    /** Drop one account from the waiver set — the inverse of a waive, from the menu or an Undo. */
    const removeWaiver = useCallback(
        (key: string) =>
            saveAcknowledged((current) => {
                const next = new Set(current);
                next.delete(key);
                return next;
            }),
        [saveAcknowledged]
    );

    const documentsByUuid = useMemo(
        () => new Map(documents.map((document: Document) => [document.uuid, document])),
        [documents]
    );

    const accounts = coverage?.accounts ?? [];
    const activeAccount = accounts.find((account) => accountKey(account) === activeAccountKey) ?? null;

    /**
     * This step's uploads whose document still exists. Filtered against the live document list so
     * a file deleted elsewhere disappears here too rather than lingering as a permanently
     * "processing" row, or as a card naming a file the member can no longer find.
     */
    const listedUploads = useMemo(
        () => uploadedStatements.filter((entry) => documentsByUuid.has(entry.member_document_id)),
        [uploadedStatements, documentsByUuid]
    );

    /**
     * Files this step uploaded for the open account, newest last.
     *
     * A stable array identity matters: it is the sole dependency of the modal's uuid memos,
     * which in turn gate whether every file's verdict is re-derived on each render.
     */
    const activeFiles = useMemo<StatementFile[]>(() => {
        if (!activeAccount) {
            return [];
        }
        const key = accountKey(activeAccount);
        return listedUploads.filter((entry) => entry.account_key === key).map(toStatementFile);
    }, [activeAccount, listedUploads]);

    /**
     * Every uploaded statement, across all accounts — what the processing banner watches.
     *
     * Not `activeFiles`: that is scoped to the open card, and the banner's whole purpose is to
     * speak for work still running when no card is open at all.
     */
    const allFiles = useMemo(() => listedUploads.map(toStatementFile), [listedUploads]);

    // Only the awaiting half is wanted here; the run a member is told about is the banner's, and
    // it watches the same list through its own mount of this hook.
    const { awaitingFiles } = useAwaitingStatements(allFiles);

    /**
     * Which account cards have a statement still being read.
     *
     * Derived from the same hook the banner uses, so the two cannot contradict each other — a
     * spinner on a card with no banner above it, or the reverse, reads as a bug rather than as
     * two independent facts.
     *
     * Keyed by account rather than counted: the card only asks whether *it* is waiting.
     */
    const processingAccountKeys = useMemo(() => {
        const accountKeyByUuid = new Map(
            uploadedStatements.map((entry) => [entry.member_document_id, entry.account_key])
        );
        return new Set(
            awaitingFiles
                .map((file) => accountKeyByUuid.get(file.documentUuid))
                .filter((key): key is string => key !== undefined)
        );
    }, [awaitingFiles, uploadedStatements]);

    /**
     * Which months each file currently counts for, straight from coverage.
     *
     * The server owns this now: `reassign-periods` moves the `BankStatementTracker` row, and
     * `held_periods` is that row read back. Deriving it here rather than storing a parallel
     * copy in the step blob is what stops the two from disagreeing — the blob version could
     * only ever be a guess about what the backend did with the file.
     *
     * Grouped per document rather than keyed one-per-document: a single PDF can carry several
     * monthly statements and holds a row for each, so a document legitimately appears in
     * `held_periods` more than once. Flattened, every month but the last silently disappeared.
     *
     * Scoped to the open account, which is all `held_periods` covers anyway, and memoised
     * because it feeds the modal's verdict memo.
     */
    const heldPeriodsByUuid = useMemo(
        () => groupHeldPeriods(activeAccount?.held_periods),
        [activeAccount?.held_periods]
    );

    const handleSavePeriods = useCallback(
        (assignments: StatementPeriodAssignment[]) =>
            // The open card, so the server can place a file whose workflow failed: with no
            // extraction there is no account to resolve, and the card is the member's own
            // answer to which one it belongs to.
            savePeriods(assignments, activeAccount?.bank_account_id),
        [savePeriods, activeAccount?.bank_account_id]
    );

    const handleRemove = useCallback(
        (documentUuid: string, period?: StatementPeriod) =>
            // The open card, always: a remove in its window takes the file off this card only.
            activeAccount ? removeUpload(documentUuid, activeAccount, period) : Promise.resolve(),
        [removeUpload, activeAccount]
    );

    const handleModalClose = useCallback(() => {
        setActiveAccountKey(null);
        refreshCoverageIfUploaded();
    }, [refreshCoverageIfUploaded]);

    /**
     * Both directions write immediately, with no confirmation either way.
     *
     * The waiver used to open a dialog on the way in. It now asks nothing here: the member is
     * only marking what they have, and the question of whether to proceed without those months
     * belongs at the point they actually proceed. The step raises it on Complete instead, where
     * it can speak for every outstanding account at once rather than one card at a time.
     *
     * What it does do is confirm afterwards, with an Undo. Waiving is the one action on this card
     * that silently removes an ask — nothing on the card grows, a count shrinks — and the member
     * has no other signal that the click landed. The toast raises the label, since `saveAcknowledged`
     * is deliberately label-free: it takes account keys, and threading a name through the shared
     * hook for one message's sake would put a copy of the card's naming rule inside the writes.
     */
    const handleAcknowledgeChange = useCallback(
        async (account: AccountStatementCoverage, acknowledged: boolean) => {
            const key = accountKey(account);
            // Before the write, and in both directions: whatever this account's toast was saying,
            // the member has just contradicted it. After an un-waive its Undo would remove a
            // waiver already gone; after a re-waive it would strike down the *new* one.
            waiverToasts.dismiss(key);

            const landed = acknowledged
                ? await saveAcknowledged((current) => new Set(current).add(key))
                : await removeWaiver(key);

            // Only the waive direction, and only once it has landed. Un-waiving from the menu is
            // already the undo and needs no toast of its own, and a failed write has raised the
            // error toast — a success beside it would contradict it.
            //
            // The last two are separate from the first two: those are about what happened, these
            // about whether there is still anyone to tell — see `WaiverToasts`.
            if (!acknowledged || !landed || !isMounted.current || waiverToasts.isSuspended()) {
                return;
            }

            // Named and lowercase, like `DocumentActions`' own: notistack hands the key back at
            // render time, and an anonymous arrow returning JSX here reads to
            // `react/no-unstable-nested-components` as a component defined during render.
            const undoAction = (snackbarKey: SnackbarKey) => (
                <UndoWaiver
                    snackbarKey={snackbarKey}
                    // Closes over the account key alone, never over a waiver set: the updater is
                    // handed the freshest set inside the write queue. A second account waived
                    // while this toast is still up is therefore left alone by this Undo, and its
                    // own toast undoes only itself.
                    onUndo={async () => {
                        const undone = await removeWaiver(key);
                        // Identity-guarded: a second waive for this account can claim the slot
                        // while this undo is in flight, and that entry speaks for a waiver still
                        // standing. Dismissing it blind would take down the wrong toast.
                        if (undone && waiverToasts.keyFor(key) === snackbarKey) {
                            waiverToasts.dismiss(key);
                        }
                        return undone;
                    }}
                />
            );

            const toastKey = enqueueSnackbar(
                // Wrapped rather than passed as a string: the theme puts `body2` on the alert's
                // message slot, and the design's body is 16/24. Scoped here rather than fixed in
                // `SnackbarProvider` because that slot styles every toast in the app.
                <Typography variant="body1paragraph">{waiverToastMessage(statementAccountLabel(account))}</Typography>,
                {
                    variant: 'info',
                    autoHideDuration: WAIVER_SNACKBAR_DURATION_MS,
                    action: undoAction,
                    // `CustomAlert` hardcodes `filled`; the design is the `standard` info surface.
                    // `alignItems` is not spare either: MUI pins `.MuiAlert-action` to
                    // `flex-start`, so once the message wraps the buttons ride up to line one.
                    alertProps: {
                        variant: 'standard',
                        sx: { maxWidth: WAIVER_SNACKBAR_MAX_WIDTH, alignItems: 'center' },
                    },
                }
            );
            // `track` dismisses whatever held this slot before claiming it. That matters after
            // the await as well as before: two waives of one account overlap while the menu still
            // reads the pre-waiver server state, and the loser would otherwise be overwritten
            // rather than closed.
            waiverToasts.track(key, toastKey);
        },
        [saveAcknowledged, enqueueSnackbar, waiverToasts, removeWaiver]
    );

    if (isCoveragePending) {
        return (
            <Stack spacing={2}>
                {[1, 2].map((key) => (
                    <Skeleton key={key} variant="rectangular" height={72} />
                ))}
            </Stack>
        );
    }

    // Never render "we could not look" as an all-clear, and never as silence — without
    // this the empty-accounts branch below swallows it and the section just disappears.
    // Payload-scoped rather than error-flag-scoped on purpose; the rationale lives on
    // `isCoverageUsable`, and the readiness hook asks the same question through it.
    if (!isCoverageUsable(coverage)) {
        return <Alert severity="warning">{COVERAGE_UNAVAILABLE_MESSAGE}</Alert>;
    }

    // The accounts loaded, but the books start date has not resolved, so every
    // `required_periods` is empty. Kept separate from the branch above on purpose: that one
    // reports a lookup that failed, and saying so here would be untrue. Rendering the cards
    // is worse still — nothing can be credited against an empty required set, so every
    // upload would come back `notNeeded`, which shows the member no message at all.
    if (coverage.coverage_start == null) {
        return (
            <Alert severity="info">
                We&apos;re still working out which statements we need. Check back shortly — there&apos;s nothing to
                upload yet.
            </Alert>
        );
    }

    if (accounts.length === 0) {
        return null;
    }

    const rangeLabel = formatMonthRange(accounts[0]?.required_periods ?? []);

    return (
        <Box>
            <Stack spacing={0.5} sx={{ mb: 2 }}>
                <Typography variant="h6" color="text.primary">
                    {rangeLabel ? `Bank statements from ${rangeLabel}` : 'Bank statements'}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                    These ensure complete and accurate financial records
                </Typography>
            </Stack>

            <StatementProcessingBanner files={allFiles} />

            <Box sx={{ borderRadius: 3, border: 1, borderColor: 'grey.100', overflow: 'hidden' }}>
                {accounts.map((account, index) => {
                    const key = accountKey(account);
                    return (
                        <Box
                            key={key}
                            sx={{
                                p: 3,
                                borderBottom: index < accounts.length - 1 ? 1 : 'none',
                                borderBottomColor: 'grey.100',
                            }}>
                            <StatementAccountCard
                                account={account}
                                onUploadClick={() => setActiveAccountKey(key)}
                                isAcknowledged={acknowledgedAccounts.has(key)}
                                onAcknowledgeChange={(acknowledged) => handleAcknowledgeChange(account, acknowledged)}
                                isProcessing={processingAccountKeys.has(key)}
                                filesUploadedElsewhere={filesUploadedElsewhere({
                                    account,
                                    accounts,
                                    uploads: listedUploads,
                                })}
                            />
                        </Box>
                    );
                })}
            </Box>

            {activeAccount && (
                <VerifiedStatementUploadModal
                    open
                    onClose={handleModalClose}
                    account={activeAccount}
                    files={activeFiles}
                    documentsByUuid={documentsByUuid}
                    onRemove={handleRemove}
                    heldPeriodsByUuid={heldPeriodsByUuid}
                    onSavePeriods={handleSavePeriods}
                    onUploadSuccess={(uploaded) => saveUploads(uploaded ?? [], activeAccount)}
                    // This step's member is mid-onboarding and about to trigger the transaction
                    // fan-out, so leaving months behind is worth one question. The back-books
                    // drawer's member is answering a standing task and gets no such friction.
                    confirmIncompleteExit
                    // Nothing here discards, so the footer is a single Close and every exit
                    // writes. The back-books drawer keeps Save/Cancel — see the prop's docs.
                    commitOnExit
                />
            )}
        </Box>
    );
}
